import { Eye, Layers, ScanSearch, ShieldCheck } from 'lucide-react';

import {
  Band,
  CtaArrow,
  ctaClasses,
  Lead,
  Panel,
  SectionTitle,
  StatGrid,
} from '@/components/bedrock';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { Container, PageHero, Reveal, SectionHeader } from '@/components/marketing-ui';
import { useT } from '@/core/I18nProvider';
import { FeaturesGrid } from '@/features/features-grid';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import { ActionVisual } from './showcase/ActionVisual';
import { DiscoveryVisual } from './showcase/DiscoveryVisual';
import { GovernVisual } from './showcase/GovernVisual';
import { Pillar } from './showcase/Pillar';
import { SurfacesVisual } from './showcase/SurfacesVisual';

import './showcase/showcase.css';

import type { ReactElement } from 'react';

/**
 * `/features` — the page that has to do the selling.
 *
 * ## What it replaced, and why the old shape could not be fixed by editing
 *
 * The page was a masthead, the fourteen-capability grid, and a CTA. Every
 * capability was true and every one was a peer: fourteen equal cards, in
 * engineering nouns ("Drift detection", "Verify-intent SDKs"), with nothing
 * larger than a paragraph anywhere on the page. A reader could finish it
 * without ever seeing the thing that makes the product different from a
 * chatbot, because that thing was card three of fourteen at the same weight as
 * bundle size.
 *
 * A feature list answers "what is in it". A buyer arrives asking "what is it,
 * why is it not the thing I already rejected, and can I let it near my
 * customers". Those are three questions and the page now has a shape for them:
 *
 * 1. **Four pillars, big.** Each one is a claim, three specifics, and a
 *    diagram of the mechanism actually happening. These are the four things
 *    that are hard to believe from a sentence — so they are shown.
 * 2. **The full grid, after.** The fourteen capabilities keep their place, but
 *    as the answer to "what else", not as the page's opening argument.
 * 3. **The numbers, then the close.**
 *
 * ## Everything moving here is decoration over prose that already says it
 *
 * The four visuals are `aria-hidden` and each sits beside the sentence it
 * illustrates. Nobody is shown a diagram they cannot read and left to infer the
 * claim from it, and turning every animation off (reduced motion, no JS, a
 * crawler) leaves the page complete — the visuals render their FINAL frame,
 * not their empty one. See `use-stepped-loop.ts`.
 */
const PILLARS = [
  { id: 'reads', icon: ScanSearch, hue: 'cyan', pointCount: 3, visual: <DiscoveryVisual /> },
  { id: 'acts', icon: Eye, hue: 'blue', pointCount: 3, visual: <ActionVisual /> },
  { id: 'governs', icon: ShieldCheck, hue: 'violet', pointCount: 3, visual: <GovernVisual /> },
  { id: 'reaches', icon: Layers, hue: 'purple', pointCount: 3, visual: <SurfacesVisual /> },
] as const;

export function Features(): ReactElement {
  const { t } = useT();
  useLocalizedSeoMeta({
    title: t('features.page.title'),
    description: t('features.page.description'),
    path: '/features',
  });

  return (
    <main id="main" tabIndex={-1} className="bg-[color:var(--bd-page)]">
      <PageHero
        eyebrow={t('features.hero.eyebrow')}
        title={t('features.hero.title')}
        description={t('features.hero.lead')}
      />

      {/* ── The one-line contrast, immediately under the hero.
             It exists because every reader arrives with a prior — they have
             seen a support chatbot — and the fastest way past it is to name it
             and say what is different, before the page asks for any more
             attention. */}
      <Band as="section" tone="page" space="sm" aria-label={t('features.contrast.label')}>
        <Container width="prose">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {(['old', 'new'] as const).map((side) => (
              <Panel
                key={side}
                accent={side === 'new'}
                className={`flex flex-col gap-2 p-6 ${side === 'new' ? '' : 'opacity-75'}`}
              >
                <p className="font-mono rtl:font-sans text-[10px] uppercase tracking-[0.14em] text-[color:var(--bd-subtle)] rtl:normal-case rtl:tracking-normal">
                  {t(`features.contrast.${side}.label`)}
                </p>
                <p className="text-[1.02rem] leading-[1.55] text-[color:var(--bd-ink)]">
                  {t(`features.contrast.${side}.body`)}
                </p>
              </Panel>
            ))}
          </div>
        </Container>
      </Band>

      {/* ── The four pillars. */}
      <Band as="section" tone="page" space="lg" aria-labelledby="pillars-heading">
        <SectionHeader
          id="pillars-heading"
          eyebrow={t('features.pillars.eyebrow')}
          sub={t('features.pillars.sub')}
        >
          {t('features.pillars.heading')}
        </SectionHeader>

        <div className="mt-16 flex flex-col gap-20 lg:gap-28">
          {PILLARS.map((pillar, index) => (
            <Pillar
              key={pillar.id}
              id={pillar.id}
              icon={pillar.icon}
              hue={pillar.hue}
              pointCount={pillar.pointCount}
              index={index}
              visual={pillar.visual}
            />
          ))}
        </div>
      </Band>

      {/* ── The numbers, on the dark band — the page's one hard-edged block,
             sitting between the argument and the inventory. */}
      <Band as="section" tone="band" space="md" ambient aria-labelledby="proof-heading">
        <SectionHeader
          id="proof-heading"
          eyebrow={t('features.proof.eyebrow')}
          sub={t('features.proof.sub')}
          onDark
        >
          {t('features.proof.heading')}
        </SectionHeader>
        <div className="mt-10">
          <StatGrid
            stats={[
              {
                value: t('features.proof.stat.languages.value'),
                label: t('features.proof.stat.languages.label'),
              },
              {
                value: t('features.proof.stat.bundle.value'),
                label: t('features.proof.stat.bundle.label'),
                accent: true,
              },
              {
                value: t('features.proof.stat.region.value'),
                label: t('features.proof.stat.region.label'),
              },
              {
                value: t('features.proof.stat.idp.value'),
                label: t('features.proof.stat.idp.label'),
              },
            ]}
          />
        </div>
      </Band>

      {/* ── The full inventory.
             `FeaturesGrid` renders four family bands and no header of its own —
             the page used to supply one from its masthead, which is exactly
             what the rebuild took away. It gets its own here instead, so the
             fourteen capabilities are introduced as "and here is the rest"
             rather than starting mid-sentence under the numbers. */}
      <Band as="section" tone="page" space="md" aria-labelledby="inventory-heading">
        <SectionHeader
          id="inventory-heading"
          eyebrow={t('features.grid.eyebrow')}
          sub={t('features.grid.subheading')}
        >
          {t('features.grid.heading')}
        </SectionHeader>
      </Band>
      <FeaturesGrid />

      {/* ── Close. */}
      <Band as="section" tone="band" space="md" aria-label={t('features.page.footerNavLabel')}>
        <Container width="prose" className="text-center">
          <SectionTitle onDark className="mx-auto max-w-2xl text-[clamp(1.5rem,2.6vw,2rem)]">
            {t('features.page.closingHeading')}
          </SectionTitle>
          <Lead onDark className="mx-auto mt-4 max-w-xl">
            {t('features.page.closingBody')}
          </Lead>
          <Reveal className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link to="/contact" className={ctaClasses({ onDark: true })}>
              {t('features.page.talkToUs')}
              <CtaArrow />
            </Link>
            <Link to="/pricing" className={ctaClasses({ variant: 'secondary', onDark: true })}>
              {t('features.page.viewPricing')}
            </Link>
          </Reveal>
        </Container>
      </Band>
    </main>
  );
}
